import React, { useEffect, useState } from 'react'
import { Box, Flex, Text, Button } from '../../../../vanilla'
import {
  slotsErrorMessagingBoxBAS,
  slotsErrorMessagingButtonText,
  slotsErrorMessagingBoxPostcodeMiniCart,
  slotsErrorMessagingBoxPopover,
  slotsErrorMessagingBoxPostcode,
  slotsErrorMessagingCloseButton,
  slotsErrorMessagingBoxPostcodeMobileNav
} from './styles.css'
import useNavigation from '../../../../hooks/use-navigation'
import { useContentStore } from '../../../../store/hooks/useStore'
import { CloseIcon } from '../../../../components/icons'
type Variant = 'popover' | 'postcodeChecker' | 'mobileNav' | 'miniCart' | 'bookASlot' | 'default'
interface SlotsErrorMessagingProps {
  error?: boolean
  variant?: Variant
  handleClose?: () => void
  buttonAction?: () => void
}
// Maps variant to CSS class
const classNameMap: Record<Variant, string> = {
  popover: slotsErrorMessagingBoxPopover,
  postcodeChecker: slotsErrorMessagingBoxPostcode,
  mobileNav: slotsErrorMessagingBoxPostcodeMobileNav,
  miniCart: slotsErrorMessagingBoxPostcodeMiniCart,
  bookASlot: slotsErrorMessagingBoxBAS,
  default: slotsErrorMessagingBoxBAS,
}
// Determine fallback messages for each scenario
function getFallbackText(error?: boolean, variant: Variant = 'default') {
  if (variant !== 'default') {
    return "We're experiencing an issue with our postcode checker. Please try again after a few minutes."
  }
  return error
    ? 'Delivery slots are temporarily unavailable. Please try again after a few minutes.'
    : 'Sorry, there are currently no slots available. Try again later or check your local store.'
}
// Determine the CMS key to fetch
function getCmsKey(error?: boolean, variant: Variant = 'default') {
  if (variant !== 'default') return 'postcode-checker-error-text'
  return error ? 'delivery-slots-error-text' : 'delivery-slots-unavailable-text'
}
// Hook to fetch message from CMS
function useErrorMessage(key: string, fallback: string) {
  const cms = useContentStore()
  const [message, setMessage] = useState(fallback)
  useEffect(() => {
    (async () => {
      if (!cms.contentById[key]) {
        await cms.fetchItemByKey(key)
      }
      const content = cms.contentById[key]
      setMessage(content?.text || fallback)
    })()
  }, [key])
  return message
}
const SlotsErrorMessaging = ({
  error,
  variant = 'default',
  handleClose,
  buttonAction,
}: SlotsErrorMessagingProps) => {
  const navigate = useNavigation()
  const cmsKey = getCmsKey(error, variant)
  const fallbackText = getFallbackText(error, variant)
  const errorMessage = useErrorMessage(cmsKey, fallbackText)
  const className = classNameMap[variant]
  const isPostcodeVariant = variant !== 'default'
  const dataTestSelectorPrefix = isPostcodeVariant ? 'postcode-checker-error' : 'delivery-slots-error'
  const handleButtonClick = () => {
    if (buttonAction) {
      buttonAction()
    } else if (error && typeof window !== 'undefined') {
      window.location.reload()
    } else {
      navigate('/store-finder')
    }
  }
  return (
    <Flex className={className} data-test-selector={dataTestSelectorPrefix}>
      {variant === 'popover' && (
        <Box
          className={slotsErrorMessagingCloseButton}
          as="button"
          aria-label="Close"
          data-test-selector="postcode-checker-error-close"
          onClick={() => handleClose?.()}
        >
          <CloseIcon boxSize="30px" />
        </Box>
      )}
      <Flex
        flexDirection={variant === 'postcodeChecker' ? 'row' : 'column'}
        gap="10px"
        height="full"
        alignItems={variant === 'postcodeChecker' ? 'center' : 'flex-start'}
        justifyContent={variant === 'miniCart' ? 'space-between' : 'center'}
      >
        <Text
          variant="text3"
          lineHeight={variant === 'postcodeChecker' ? ['none', 'shorter'] : 'inherit'}
          data-test-selector={`${dataTestSelectorPrefix}-message`}
        >
          {errorMessage}
        </Text>
        <Button
          variant="primary"
          aria-label={error ? 'Try again' : 'Store Finder'}
          onClick={handleButtonClick}
          style={{ maxHeight: variant === 'postcodeChecker' ? '38px' : 'auto' }}
          width="98px"
          data-test-selector={`${dataTestSelectorPrefix}-button`}
        >
          <Text
            variant="text3"
            className={slotsErrorMessagingButtonText}
            data-test-selector={
              error || isPostcodeVariant ? 'try-again' : 'store-finder'
            }
            position="relative"
          >
            {error || isPostcodeVariant ? 'Try again' : 'Store Finder'}
          </Text>
        </Button>
      </Flex>
    </Flex>
  )
}
export default SlotsErrorMessaging